import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Loader2,
  SlidersHorizontal,
} from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Input } from '@/shared/components/ui/input'
import { Badge } from '@/shared/components/ui/badge'
import { DatePicker } from '@/shared/components/ui/date-picker'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { DataTable } from '@/admin/components/data-table'
import type { DataTableColumn } from '@/admin/components/data-table'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  useReports,
  useReportPreview,
  type ReportSummary,
} from '@/admin/hooks/use-reports'
import { useReportExport } from '@/admin/hooks/use-report-export'

/** Stringify a raw report cell value for display (matches the export layer). */
function formatCell(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) return value.map((v) => String(v)).join('; ')
  return String(value)
}

/** Drop empty entries so the request only carries applied params. */
function cleanParams(params: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== ''),
  )
}

interface ReportFilterModalProps {
  report: ReportSummary
  canExport: boolean
  onClose: () => void
}

/**
 * Advanced filtering + preview for one report. Date window plus the report's
 * declared filters drive a live preview and the filtered exports. All columns
 * and filters come from the API - nothing about a specific report is hardcoded.
 */
function ReportFilterModal({ report, canExport, onClose }: ReportFilterModalProps) {
  const { t } = useTranslation()
  const { exportReport, exportingKey } = useReportExport()
  const [after, setAfter] = useState('')
  const [before, setBefore] = useState('')
  const [filterValues, setFilterValues] = useState<Record<string, string>>({})
  const [appliedParams, setAppliedParams] = useState<Record<string, string> | null>(null)
  // The date fields open their calendar on focus; if the Dialog autofocuses the
  // first field, that calendar overlays the action buttons. Point initial focus
  // at a neutral sentinel so the modal opens with nothing expanded.
  const initialFocusRef = useRef<HTMLDivElement>(null)

  const draftParams = useMemo(
    () => cleanParams({ created_after: after, created_before: before, ...filterValues }),
    [after, before, filterValues],
  )

  const preview = useReportPreview(
    report.key,
    appliedParams ?? {},
    appliedParams !== null,
  )

  const columns: Array<DataTableColumn<Record<string, unknown>>> = useMemo(
    () =>
      (preview.data?.columns ?? report.columns).map((c) => ({
        id: c.key,
        header: c.label,
        cell: (row: Record<string, unknown>) => formatCell(row[c.key]),
      })),
    [preview.data?.columns, report.columns],
  )

  // Report rows have no natural id; stamp a stable per-row key so DataTable's
  // rowKey (called with the row only) never collides.
  const rows = useMemo(
    () =>
      (preview.data?.results ?? []).map((row, index) => ({
        ...row,
        __rowid: String(index),
      })),
    [preview.data?.results],
  )

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="w-[min(1100px,95vw)] max-w-[95vw] sm:max-w-[1100px]"
        initialFocus={initialFocusRef}
      >
        <div ref={initialFocusRef} tabIndex={-1} className="outline-none" />
        <DialogHeader>
          <DialogTitle>{report.title}</DialogTitle>
          <DialogDescription>{report.description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Filters */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="flex flex-col gap-1">
              <Label>{t('reports.filter_from', 'From')}</Label>
              <DatePicker
                value={after}
                onChange={setAfter}
                placeholder={t('reports.filter_any_date', 'Any date')}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label>{t('reports.filter_to', 'To')}</Label>
              <DatePicker
                value={before}
                onChange={setBefore}
                placeholder={t('reports.filter_any_date', 'Any date')}
              />
            </div>
            {report.filters.map((filter) => (
              <div key={filter.key} className="flex flex-col gap-1">
                <Label>{filter.label}</Label>
                {filter.type === 'select' && filter.options ? (
                  <Select
                    value={filterValues[filter.key] ?? ''}
                    onValueChange={(value) =>
                      setFilterValues((prev) => ({ ...prev, [filter.key]: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('reports.filter_all', 'All')} />
                    </SelectTrigger>
                    <SelectContent>
                      {filter.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : filter.type === 'date' ? (
                  <DatePicker
                    value={filterValues[filter.key] ?? ''}
                    onChange={(value) =>
                      setFilterValues((prev) => ({ ...prev, [filter.key]: value }))
                    }
                    placeholder={t('reports.filter_any_date', 'Any date')}
                  />
                ) : (
                  <Input
                    value={filterValues[filter.key] ?? ''}
                    onChange={(e) =>
                      setFilterValues((prev) => ({
                        ...prev,
                        [filter.key]: e.target.value,
                      }))
                    }
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => setAppliedParams(draftParams)}>
              {t('reports.run_preview', 'Run preview')}
            </Button>
            {canExport && (
              <>
                <Button
                  variant="outline"
                  onClick={() => void exportReport(report.key, 'csv', draftParams)}
                  disabled={exportingKey === `${report.key}:csv`}
                >
                  {exportingKey === `${report.key}:csv` ? (
                    <Loader2 className="animate-spin" size={15} />
                  ) : (
                    <Download size={15} />
                  )}
                  {t('reports.export_csv', 'CSV')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => void exportReport(report.key, 'xlsx', draftParams)}
                  disabled={exportingKey === `${report.key}:xlsx`}
                >
                  {exportingKey === `${report.key}:xlsx` ? (
                    <Loader2 className="animate-spin" size={15} />
                  ) : (
                    <FileSpreadsheet size={15} />
                  )}
                  {t('reports.export_excel', 'Excel')}
                </Button>
              </>
            )}
            {preview.data && (
              <span className="text-[13px] text-[var(--ink-3)]">
                {t('reports.row_count', {
                  count: preview.data.count,
                  defaultValue: '{{count}} rows',
                })}
              </span>
            )}
          </div>

          {/* Preview */}
          {appliedParams !== null && (
            <div className="max-h-[420px] overflow-auto">
              <DataTable<Record<string, unknown>>
                tableId={`report-preview-${report.key}`}
                columns={columns}
                data={rows}
                isLoading={preview.isPending}
                isFetching={preview.isFetching}
                rowKey="__rowid"
                emptyMessage={t('reports.no_rows', 'No rows match these filters.')}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Expandable Reports section shown directly below the dashboard header. Gated to
 * admins by `reports.view` at the call site. Lists the tenant's report
 * definitions with one-click CSV / Excel exports and an advanced filter modal.
 */
export function ReportingSection() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const [expanded, setExpanded] = useState(false)
  const [activeReport, setActiveReport] = useState<ReportSummary | null>(null)
  const { data: reports, isPending, isError } = useReports()
  const { exportReport, exportingKey } = useReportExport()

  const canExport = can('reports.export')
  const count = reports?.length ?? 0

  // Deep link (`/dashboard#reports`, e.g. the "Business Reporting" favourite):
  // open the section and bring it into view once the catalogue has loaded.
  const { hash } = useLocation()
  const sectionRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (hash !== '#reports' || count === 0) return
    setExpanded(true)
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [hash, count])

  // Data-driven visibility: the Reports panel only has meaning once the tenant
  // has a report catalogue. Hide it for tenants with none configured (e.g.
  // tenants that don't use standard reports) rather than surfacing an empty
  // "No reports are configured" panel. No tenant/flag coupling - a tenant gets
  // Reports by having reports, exactly like OC does.
  //
  // Don't render while the fetch is in flight either: rendering during loading
  // and then unmounting once it settles empty would be a flash-and-vanish
  // layout shift for the (common) no-catalogue tenant. A settled error still
  // renders, so a real failure surfaces rather than being silently swallowed.
  if (isPending) {
    return null
  }
  if (!isError && count === 0) {
    return null
  }

  return (
    <section
      id="reports"
      ref={sectionRef}
      className="mb-6 scroll-mt-6 rounded-[12px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)]"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown size={16} className="text-[var(--ink-3)]" />
          ) : (
            <ChevronRight size={16} className="text-[var(--ink-3)]" />
          )}
          <span className="text-[15px] font-medium text-[var(--ink)]">
            {t('reports.section_title', 'Reports')}
          </span>
          {count > 0 && (
            <Badge variant="secondary">{count}</Badge>
          )}
        </span>
        <span className="text-[13px] text-[var(--ink-3)]">
          {t('reports.section_hint', 'Standard reports & exports')}
        </span>
      </button>

      {expanded && (
        <div className="border-t-[0.5px] border-[var(--rule)] p-4">
          {isError ? (
            <div className="py-6 text-[13px] text-[var(--status-error)]">
              {t('reports.load_error', 'Could not load reports.')}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {reports!.map((report) => (
                <div
                  key={report.key}
                  className="flex flex-col gap-3 rounded-[10px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-4"
                >
                  <div className="flex flex-col gap-1">
                    <span className="flex items-center gap-2 text-[14px] font-medium text-[var(--ink)]">
                      {report.title}
                      {report.requires_finance && (
                        <Badge variant="outline">
                          {t('reports.finance_badge', 'Finance')}
                        </Badge>
                      )}
                    </span>
                    <span className="text-[12px] leading-relaxed text-[var(--ink-3)]">
                      {report.description}
                    </span>
                  </div>
                  <div className="mt-auto flex flex-wrap items-center gap-2">
                    {canExport && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void exportReport(report.key, 'csv')}
                          disabled={exportingKey === `${report.key}:csv`}
                        >
                          {exportingKey === `${report.key}:csv` ? (
                            <Loader2 className="animate-spin" size={14} />
                          ) : (
                            <Download size={14} />
                          )}
                          {t('reports.export_csv', 'CSV')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void exportReport(report.key, 'xlsx')}
                          disabled={exportingKey === `${report.key}:xlsx`}
                        >
                          {exportingKey === `${report.key}:xlsx` ? (
                            <Loader2 className="animate-spin" size={14} />
                          ) : (
                            <FileSpreadsheet size={14} />
                          )}
                          {t('reports.export_excel', 'Excel')}
                        </Button>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveReport(report)}
                    >
                      <SlidersHorizontal size={14} />
                      {t('reports.filter', 'Filter')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeReport && (
        <ReportFilterModal
          report={activeReport}
          canExport={canExport}
          onClose={() => setActiveReport(null)}
        />
      )}
    </section>
  )
}
